import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Options, Profile } from '../../profile.model';

import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

// Services
import { TranslateService } from '@ngx-translate/core';
import { TrackerService } from '@app/shared/services/tracker.service';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import {
  isItemSelected,
  ProfileCollectionUtilsService,
} from './profile-collection-reactive-store/utils';

import { ProfileCollectionItem } from '@app/profile/profile-api.model';
import { ProfileService } from '@app/profile/services/profile.service';

import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { A11yService } from '@app/shared/services/a11y.service';
import { LDFlagsService, WindowLayoutService } from '@dg/shared-services';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import {
  ProfileCollectionBookmarkService,
  ProfileCollectionFacade,
  AddKey,
  ProfileCollectionViewModel,
} from '@app/profile/components/profile-collection/profile-collection-reactive-store';

@Component({
  selector: 'dgx-profile-collection',
  templateUrl: './profile-collection.component.html',
  styleUrls: ['./profile-collection.component.scss'],
})
export class ProfileCollectionComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  public readonly CLASS_NAME = 'ProfileCollectionComponent';

  @Input() public options: Options;
  @Input() public owner: Profile;

  @ViewChild('addButton') public addButton: TemplateRef<any>;
  @ViewChild('addExperienceButton')
  public addExperienceButton: TemplateRef<any>;

  @ViewChildren('itemTile') public itemTiles: QueryList<any>;

  public vm$: Observable<ProfileCollectionViewModel>;

  public currentUserItemId = -1;
  public indexesOfCardsToExpand = [];

  public isMobile = this.windowLayoutService.isMobile;
  public collectionAnnouncement = '';
  public i18n = this.translateService.instant([
    'UserController_Type',
    'UserController_NoResults',
    'UserController_For',
    'UserController_In',
    'UserController_BulkEditSelectLabel',
    'Core_ClearFilters',
    'ProfileCtrl_NoExactMatches',
    'ProfileCtrl_NoExactMatchesHelp',
    'dgProfileOverview_NonOwnerEmptyStateTitle',
    'dgProfileOverview_NonOwnerEmptyStateBody',
    'ProfileCtrl_OwnerEmptyStateTitle',
    'ProfileCtrl_OwnerEmptyStateBody',
    'Ext_ErrorMessage',
    'Core_PleaseTryAgain',
    'UserController_Collection',
    'UserController_CollectionDescription',
    'Core_Select',
    'Core_Cancel',
    'dgProfileOverview_LearningTitle',
    'Core_ExperiencesDisplayName',
    'Core_Pathways',
    'Core_Achievements',
    'Core_FindContent',
    'Core_Plans',
    'Core_Add',
    'UserController_AddToCollectionSelf',
    'UserController_AddToCollectionOther',
    'Core_Learnings',
    'UserController_AddToCollectionDescription',
    'Core_ShowMore',
  ]);

  public emptyTileCount = 12; // 4 per row vs 3 per row
  public emptyTiles = [...Array(this.emptyTileCount).keys()];

  public allItemsRemovedByFiltersImage =
    '/content/img/emptystate/profile-settings.svg';

  constructor(
    private translateService: TranslateService,
    private trackerService: TrackerService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private profileService: ProfileService,
    private profileCollectionFacade: ProfileCollectionFacade,
    private a11yService: A11yService,
    private ldFlagsService: LDFlagsService,
    private genericModalService: InputModalDispatcherService,
    private profileCollectionUtilsService: ProfileCollectionUtilsService,
    private windowLayoutService: WindowLayoutService,
    private profileCollectionBookmarkService: ProfileCollectionBookmarkService,
    private translate: TranslateService
  ) {
    super();
    this.vm$ = this.profileCollectionFacade.vm$.pipe(
      this.profileCollectionBookmarkService.updateUrl
    );
    if (!this.profileCollectionFacade.enabled) {
      this.profileCollectionFacade.enabled = true;
    }
  }

  public get nonOwnerEmptyStateText(): string {
    return this.translateService.instant(
      'dgProfileOverview_NonOwnerEmptyStateBody',
      {
        firstName: this.owner.name.split(' ')[0],
      }
    );
  }

  public get pathwaysOrPlansDisplayed(): boolean {
    return this.profileCollectionFacade.pathwaysOrPlansDisplayed;
  }

  public get hasError(): Observable<boolean> {
    return this.profileCollectionFacade.hasError$.pipe(
      map((hasError) => hasError)
    );
  }

  public get showExperienceTypes() {
    return this.ldFlagsService.showExperienceTypes;
  }

  public get ownerIsViewing() {
    return this.options?.ownerIsViewing;
  }

  public ngOnInit(): void {
    // Capture initial URL and update state
    this.profileCollectionBookmarkService.updateState();

    //  readFirst(this.profileCollectionFacade.getCollectionItems(false));
    this.trackerService.trackEventData({
      action: 'Profile Collection Viewed',
      properties: {
        UserId: this.owner.id,
        IsActiveLearner: this.owner.isEngaged,
        ProfileType: this.profileService.getProfileType(),
      },
    });
    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.profileCollectionFacade.enabled = false;
  }

  // Arrow function to preserve `this` when called by the content card service
  public modifyOptionsFn = (
    content: LearningResourceViewModel,
    defaultConfig: MenuViewModel[]
  ) => {
    return this.profileOverviewActionsService.modifyInputMenuOptions(
      this.ownerIsViewing,
      this.ownerIsViewing,
      content,
      defaultConfig
    );
  };

  public setClassifier(classifier: string): void {
    this.indexesOfCardsToExpand = [];
    this.profileCollectionFacade.setCurrentItemClassifier(classifier);
    this.a11yService.announcePolite(
      this.translate.instant('A11y_CollectionChangeAnnouncement', {
        collectionName: classifier,
      })
    );
    this.collectionAnnouncement = this.translateService.instant(
      'A11y_CollectionChangeAnnouncement',
      {
        collectionName: classifier,
      }
    );
  }

  public onItemClick(
    item: ProfileCollectionItem<any>,
    bulkUpdateEnabled: boolean
  ): void {
    if (bulkUpdateEnabled) {
      this.toggleItemSelection(item);
    }
  }

  public showAddModal(addKey: AddKey): void {
    const buttonRef =
      addKey.key === 'Position' ? this.addExperienceButton : this.addButton;
    this.profileCollectionUtilsService.showAddModal(
      addKey,
      buttonRef.elementRef.nativeElement
    );
  }

  public shouldShowCheckBox(
    collectionItem: ProfileCollectionItem<any>,
    selectedItems: ProfileCollectionItem<any>[],
    bulkUpdateEnabled: boolean
  ): boolean {
    return (
      (bulkUpdateEnabled ||
        isItemSelected(collectionItem, selectedItems) ||
        this.currentUserItemId === collectionItem.userItemId) &&
      this.ownerIsViewing
    );
  }

  public showCheckbox(id: number): void {
    this.currentUserItemId = id;
  }

  public hideCheckbox(): void {
    this.currentUserItemId = -1;
  }

  public toggleItemSelection(collectionItem: ProfileCollectionItem<any>): void {
    this.profileCollectionFacade.toggleItemSelection(collectionItem);
  }

  // this could be better...
  public updateCollection(append: boolean): void {
    // for show more
    this.profileCollectionFacade
      .getCollectionItems(append)
      .pipe(take(1))
      .subscribe();
  }

  public addExperienceWithId(experienceId: string): void {
    this.genericModalService
      .showModal({
        inputType: 'Position',
        initialModel: {
          inputSubType: experienceId,
        },
        isCompleting: true,
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe();
  }

  public onIntersection(
    item: ProfileCollectionItem<any>,
    itemIndex: number
  ): void {
    this.trackerService.trackEventData({
      action: 'Content Impressed',
      properties: {
        location: 'Profile Collection', // Maps to ActionLocation in Amplitude
        inputType: item.content.inputType, // Maps to ContentType in Amplitude
        contentId: item.content.inputId,
        title: item.content.title,
        providerId: item.content.providerId,
        providerName: item.content.providerName,
        externalId: item.content.externalId,
        position: itemIndex,
        dateCreated: item.content.dateCreated,
      },
    });
  }

  public onClickTitleEllipses(newIndexesOfCardsToExpand: number[]) {
    if (this.indexesOfCardsToExpand.length === 0) {
      // If - no cards in any rows have been expanded yet, and the clicked row should expand
      const elementsToExpand: HTMLElement[] = newIndexesOfCardsToExpand.map(
        (index) => document.getElementById(`profileCollectionItem${index}`)
      );
      elementsToExpand.map((element: HTMLElement) =>
        element?.style.setProperty('max-height', 'none', 'important')
      );
    } else {
      // Else - new cards in the newly clicked row should be expanded and old row of cards should return to default size
      const elementsToShrink: HTMLElement[] = this.indexesOfCardsToExpand.map(
        (index) => document.getElementById(`profileCollectionItem${index}`)
      );
      const elementsToExpand: HTMLElement[] = newIndexesOfCardsToExpand.map(
        (index) => document.getElementById(`profileCollectionItem${index}`)
      );

      elementsToShrink.map((element: HTMLElement) =>
        element?.style.setProperty('max-height', 'revert')
      );
      elementsToExpand.map((element: HTMLElement) =>
        element?.style.setProperty('max-height', 'none', 'important')
      );
    }

    this.indexesOfCardsToExpand = newIndexesOfCardsToExpand;
  }
}
